import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, TextField, Typography } from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    HowToReg as HowToRegIcon,
    Email as EmailIcon,
    CreditCard as CreditCardIcon,
    Groups as GroupsIcon,
    ArrowForward as ArrowForwardIcon,
    TaskAlt as TaskAltIcon,
    ErrorOutline as ErrorOutlineIcon,
    AccessTime as AccessTimeIcon,
    Refresh as RefreshIcon,
    OpenInNew as OpenInNewIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { getActiveBrandConfig } from 'src/config/environment.config';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import { useProfilContext } from 'src/Contexts/ProfilContexte';
import ButtonField from 'src/Components/Ui/ButtonField';
import Settings from 'src/Components/Settings';
import Teachr_Repository from 'src/Repositories/Teachr_Repository';
import TeachrRepository from 'src/Repositories/TeachrRepository';
import { userTeachrSetAccountAction } from 'src/Redux/Actions/UserActions';

const SCOOP_SIGNE = "20";
const SCOOP_PAIEMENT_INITIE = "25";
const SCOOP_PAIEMENT_ECHOUE = "26";
const SCOOP_PAIEMENT_ANNULE = "27";
const SCOOP_PAIEMENT_EXPIRE = "28";
const SCOOP_VALIDE = "30";
const SCOOP_ACTIF = "40";

const CooperativeConfirmationStep: React.FC = () => {
    const { teachr, dispatch } = useProfilContext();
    const { brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const fontFamily = brandConfig.fontFamily;
    const navigate = useNavigate();
    const location = useLocation();
    const fromPayment = location.search === "?redirect=1" ? true : false;

    useEffect(() => {
        if (!fromPayment) return;

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout>;
        let attempt = 0;
        const MAX_ATTEMPTS = 10;
        const POLL_DELAY_MS = 3000;

        const fetchAndMaybeRetry = async () => {
            const refreshedUser = await TeachrRepository.getCurrentTeachr().catch(() => null);
            if (cancelled || !refreshedUser) return;

            const serialized = JSON.parse(JSON.stringify(refreshedUser));
            dispatch(userTeachrSetAccountAction(serialized));

            if (refreshedUser.scoop === SCOOP_PAIEMENT_INITIE && attempt < MAX_ATTEMPTS) {
                attempt++;
                timeoutId = setTimeout(fetchAndMaybeRetry, POLL_DELAY_MS);
            }
        };

        fetchAndMaybeRetry();

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [dispatch, fromPayment]);

    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [isPaymentLoading, setIsPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const scoop = teachr?.scoop;
    const handleInitiatePayment = async () => {
        setIsPaymentLoading(true);
        setPaymentError('');
        try {
            const response: any = await Teachr_Repository.initiateAdhesionPayment(teachr.id!);
            const checkoutUrl = response?.checkoutUrl ?? teachr?.mollieCheckoutUrl;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            } else {
                setPaymentError('Impossible de créer le lien de paiement. Veuillez réessayer.');
            }
        } catch {
            setPaymentError('Une erreur est survenue lors de la création du paiement. Veuillez réessayer.');
        } finally {
            setIsPaymentLoading(false);
        }
    };
    const handleValidateCode = async () => {
        const trimmed = code.trim();

        if (!trimmed) {
            setCodeError('Veuillez saisir votre code de validation.');
            return;
        }
        if (trimmed.length !== ('SCOOPVTSAP-' + teachr.invitationCode!).length) {
            setCodeError('Le code de validation est incorrect. Veuillez vérifier et réessayer.');
            return;
        }
        setIsValidating(true);
        setCodeError('');
        try {
            await Teachr_Repository.teachrValidateAccountWithCode(teachr.id!, trimmed);
            setIsValidated(true);
        } catch {
            setCodeError('Code invalide ou expiré. Vérifiez votre e-mail et réessayez.');
        } finally {
            setIsValidating(false);
        }
    };

    if (isValidated || scoop === SCOOP_ACTIF) {
        return (
            <Settings
                title="Bienvenue dans la coopérative !"
                subtitle="Votre compte coopératif est maintenant actif"
                icon={<TaskAltIcon sx={{ color: '#16a34a', fontSize: 28 }} />}
                height="100%"
            >
                <Grid item xs={12}>
                    <Box sx={{
                        p: 4,
                        textAlign: 'center',
                        bgcolor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 3,
                    }}>
                        <Box sx={{
                            width: 80, height: 80, borderRadius: '50%',
                            bgcolor: '#dcfce7', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', mx: 'auto', mb: 2.5,
                        }}>
                            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 48 }} />
                        </Box>
                        <Typography variant="h5" fontWeight="800" color="#166534" fontFamily={fontFamily} mb={1}>
                            Félicitations !
                        </Typography>
                        <Typography variant="body1" color="#166534" fontFamily={fontFamily} mb={1}>
                            Votre adhésion à <strong>Verified Trainer SAP</strong> est confirmée.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontFamily={fontFamily} mb={3.5}>
                            Vous pouvez désormais facturer l'ensemble de vos prestations et bénéficier
                            de l'accompagnement de la coopérative.
                        </Typography>
                        <ButtonField
                            label="Accéder à mon espace"
                            variant="contained"
                            className="font-bold"
                            startIcon={<ArrowForwardIcon />}
                            onClick={() => navigate(`/${getActiveBrandConfig()}/tableau-de-bord`)}
                        />
                    </Box>
                </Grid>
            </Settings>
        );
    }

    const paymentDone = scoop === SCOOP_VALIDE;
    const paymentError_ = scoop === SCOOP_PAIEMENT_ECHOUE || scoop === SCOOP_PAIEMENT_ANNULE || scoop === SCOOP_PAIEMENT_EXPIRE;

    const steps = [
        { icon: <CheckCircleIcon />, label: 'Document signé', done: true, active: false },
        { icon: <CreditCardIcon />, label: 'Paiement', done: paymentDone, active: !paymentDone },
        { icon: <HowToRegIcon />, label: 'Activation', done: false, active: paymentDone },
    ];
    return (
        <Settings
            title="Finalisation de votre adhésion"
            subtitle="Plus qu'une étape pour rejoindre Verified Trainer SAP"
            icon={<GroupsIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* Step progress */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'nowrap' }}>
                    {steps.map(({ icon, label, done, active }, i) => (
                        <React.Fragment key={label}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.75,
                                px: 1.5, py: 0.75, borderRadius: 10, border: '1px solid',
                                borderColor: done ? '#bbf7d0' : active ? primaryHex : 'grey.300',
                                bgcolor: done ? '#f0fdf4' : active ? `${primaryHex}12` : 'grey.50',
                                flexShrink: 0,
                            }}>
                                {React.cloneElement(icon as React.ReactElement, {
                                    sx: { fontSize: 15, color: done ? '#16a34a' : active ? primaryHex : 'text.disabled' }
                                })}
                                <Typography variant="caption" fontWeight="600" fontFamily={fontFamily}
                                    sx={{ color: done ? '#166534' : active ? primaryHex : 'text.disabled' }}>
                                    {label}
                                </Typography>
                            </Box>
                            {i < steps.length - 1 && (
                                <Box sx={{ flex: 1, height: 1, bgcolor: 'grey.300', mx: 0.5, minWidth: 12 }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            </Grid>

            {/* Document received confirmation */}
            <Grid item xs={12}>
                <Box sx={{
                    p: 2.5, borderRadius: 2,
                    border: '1px solid #bbf7d0', bgcolor: '#f0fdf4',
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                }}>
                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                    <Box>
                        <Typography variant="subtitle1" fontWeight="700" color="#166534" fontFamily={fontFamily}>
                            Votre document signé a bien été reçu
                        </Typography>
                        <Typography variant="body2" color="#166534" fontFamily={fontFamily} mt={0.5}>
                            Votre règlement intérieur signé a été transmis avec succès à la coopérative
                            {' '}<strong>Verified Trainer SAP</strong> et enregistré dans votre dossier.
                        </Typography>
                        {teachr?.proofMandat?.contentUrl && (
                            <Box mt={1.5}>
                                <a href={teachr.proofMandat.contentUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                    <ButtonField
                                        label="Télécharger mon document signé"
                                        variant="outlined"
                                        className="font-bold"
                                        startIcon={<DownloadIcon />}
                                    />
                                </a>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>

            {/* Payment section */}
            <Grid item xs={12}>
                <Box sx={{
                    p: 2.5, borderRadius: 2,
                    border: '1px solid', borderColor: paymentDone ? '#bbf7d0' : paymentError_ ? '#fecaca' : `${primaryHex}40`,
                    bgcolor: paymentDone ? '#f0fdf4' : paymentError_ ? '#fef2f2' : `${primaryHex}06`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '50%',
                            bgcolor: paymentDone ? '#dcfce7' : paymentError_ ? '#fee2e2' : `${primaryHex}1a`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            {paymentDone
                                ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22 }} />
                                : paymentError_
                                    ? <ErrorOutlineIcon sx={{ color: '#dc2626', fontSize: 22 }} />
                                    : <CreditCardIcon sx={{ color: primaryHex, fontSize: 22 }} />
                            }
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="700" color="text.primary" fontFamily={fontFamily}>
                                {paymentDone
                                    ? 'Paiement validé'
                                    : 'Paiement d\'adhésion symbolique'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>
                                Paiement unique, frais administratifs d'adhésion
                            </Typography>
                        </Box>
                    </Box>

                    {/* Montant */}
                    {!paymentDone && (
                        <Box sx={{
                            display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2.5, p: 2.5,
                            bgcolor: 'white', borderRadius: 2,
                            border: '1px solid', borderColor: 'grey.200',
                            mb: 2,
                        }}>
                            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                                <Typography variant="h3" fontWeight="900" color={primaryHex} fontFamily={fontFamily} lineHeight={1}>
                                    1€
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>
                                    paiement unique
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" fontFamily={fontFamily} flex={1}>
                                Ce paiement symbolique couvre les frais administratifs de votre adhésion à la
                                coopérative et valide définitivement votre inscription. Il est non récurrent.
                            </Typography>
                        </Box>
                    )}

                    {/* État : SCOOP_SIGNE - paiement non initié */}
                    {scoop === SCOOP_SIGNE && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" fontFamily={fontFamily} mb={2}>
                                Cliquez sur le bouton ci-dessous pour procéder au paiement sécurisé de votre adhésion via notre partenaire Mollie.
                            </Typography>
                            {paymentError && (
                                <Typography variant="body2" color="error" fontFamily={fontFamily} mb={1.5}>
                                    {paymentError}
                                </Typography>
                            )}
                            <ButtonField
                                label={isPaymentLoading ? 'Redirection…' : 'Payer 1€, Procéder au paiement'}
                                variant="contained"
                                className="font-bold"
                                startIcon={<CreditCardIcon />}
                                onClick={handleInitiatePayment}
                                disabled={isPaymentLoading}
                                loading={isPaymentLoading}
                            />
                        </Box>
                    )}

                    {/* État : SCOOP_PAIEMENT_INITIE - en attente */}
                    {scoop === SCOOP_PAIEMENT_INITIE && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: 2, borderRadius: 2, bgcolor: '#fefce8',
                                border: '1px solid #fef08a',
                            }}>
                                <AccessTimeIcon sx={{ color: '#ca8a04', fontSize: 22, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" fontWeight="700" color="#854d0e" fontFamily={fontFamily}>
                                        Paiement en cours de traitement
                                    </Typography>
                                    <Typography variant="caption" color="#854d0e" fontFamily={fontFamily}>
                                        Vous serez notifié par e-mail à <strong>{teachr.mail}</strong> dès que votre paiement sera confirmé.
                                    </Typography>
                                </Box>
                            </Box>
                            {teachr.mollieCheckoutUrl && (
                                <Box mt={1.5}>
                                    <a href={teachr.mollieCheckoutUrl!} style={{ textDecoration: 'none' }}>
                                        <ButtonField
                                            label="Rouvrir la page de paiement"
                                            variant="outlined"
                                            className="font-bold"
                                            startIcon={<OpenInNewIcon />}
                                        />
                                    </a>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* États : SCOOP_PAIEMENT_ECHOUE / ANNULE / EXPIRE - erreur/retry */}
                    {paymentError_ && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: 2, borderRadius: 2, bgcolor: '#fef2f2',
                                border: '1px solid #fecaca',
                            }}>
                                <ErrorOutlineIcon sx={{ color: '#dc2626', fontSize: 22, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" fontWeight="700" color="#991b1b" fontFamily={fontFamily}>
                                        {scoop === SCOOP_PAIEMENT_ECHOUE && 'Paiement échoué'}
                                        {scoop === SCOOP_PAIEMENT_ANNULE && 'Paiement annulé'}
                                        {scoop === SCOOP_PAIEMENT_EXPIRE && 'Lien de paiement expiré'}
                                    </Typography>
                                    <Typography variant="caption" color="#991b1b" fontFamily={fontFamily}>
                                        {scoop === SCOOP_PAIEMENT_ECHOUE && 'Une erreur est survenue lors du paiement. Veuillez réessayer.'}
                                        {scoop === SCOOP_PAIEMENT_ANNULE && 'Vous avez annulé le paiement. Vous pouvez le relancer.'}
                                        {scoop === SCOOP_PAIEMENT_EXPIRE && 'Le lien de paiement a expiré. Générez-en un nouveau.'}
                                    </Typography>
                                </Box>
                            </Box>
                            {paymentError && (
                                <Typography variant="body2" color="error" fontFamily={fontFamily}>
                                    {paymentError}
                                </Typography>
                            )}
                            <ButtonField
                                label={isPaymentLoading ? 'Redirection…' : 'Réessayer le paiement'}
                                variant="contained"
                                className="font-bold"
                                startIcon={<RefreshIcon />}
                                onClick={handleInitiatePayment}
                                disabled={isPaymentLoading}
                                loading={isPaymentLoading}
                            />
                        </Box>
                    )}

                    {/* État : SCOOP_VALIDE - paiement confirmé */}
                    {scoop === SCOOP_VALIDE && (
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1.5,
                            p: 2, borderRadius: 2, bgcolor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                        }}>
                            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22, flexShrink: 0, mt: 0.15 }} />
                            <Box>
                                <Typography variant="body2" fontWeight="700" color="#166534" fontFamily={fontFamily}>
                                    Paiement confirmé
                                </Typography>
                                <Typography variant="caption" color="#166534" fontFamily={fontFamily}>
                                    Votre code d'activation a été envoyé à <strong>{teachr.mail}</strong>. Saisissez-le ci-dessous pour activer votre compte.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Rappel email - uniquement si paiement pas encore validé */}
                    {!paymentDone && scoop !== SCOOP_PAIEMENT_INITIE && (
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                            p: 1.5, borderRadius: 1.5, mt: 2,
                            bgcolor: `${primaryHex}08`,
                            border: '1px dashed', borderColor: `${primaryHex}50`,
                        }}>
                            <EmailIcon sx={{ color: primaryHex, fontSize: 18, flexShrink: 0, mt: 0.15 }} />
                            <Typography variant="caption" fontFamily={fontFamily} color="text.secondary">
                                Après votre paiement, vous recevrez un <strong>code de validation</strong> par e-mail
                                à l'adresse <strong>{teachr.mail}</strong>. Saisissez ce code ci-dessous pour activer votre compte.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Grid>

            {/* Code validation - affiché uniquement après paiement validé (scoop = 30) */}
            {scoop === SCOOP_VALIDE && (
                <Grid item xs={12}>
                    <Box sx={{
                        p: 2.5, borderRadius: 2,
                        border: '1px solid', borderColor: 'grey.200',
                        bgcolor: 'background.paper',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                bgcolor: '#e0f2fe', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                <HowToRegIcon sx={{ color: '#0284c7', fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="700" color="text.primary" fontFamily={fontFamily}>
                                    Activer mon compte
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>
                                    Saisissez le code reçu par e-mail après paiement
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" fontFamily={fontFamily} mb={4}>
                            Entrez le code unique envoyé à{' '}
                            <strong>{teachr.mail}</strong> pour finaliser l'activation de votre compte coopératif.
                        </Typography>

                        <Box sx={{
                            display: 'flex', gap: 1.5, alignItems: 'flex-start',
                            flexDirection: { xs: 'column', sm: 'row' },
                        }}>
                            <TextField
                                value={code}
                                onChange={e => { setCode(e.target.value.toUpperCase()); setCodeError(''); }}
                                onKeyDown={e => e.key === 'Enter' && !isValidating && handleValidateCode()}
                                placeholder="Ex : A1B2-C3D4"
                                variant="outlined"
                                size="medium"
                                error={!!codeError}
                                helperText={codeError || ' '}
                                inputProps={{
                                    maxLength: 20,
                                    style: {
                                        fontFamily,
                                        letterSpacing: '0.25em',
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                        textTransform: 'uppercase',
                                    },
                                }}
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': { borderColor: primaryHex },
                                        '&:hover fieldset': { borderColor: primaryHex },
                                    },
                                    '& .MuiFormHelperText-root': { fontFamily },
                                }}
                            />
                            <Box sx={{ flexShrink: 0, mt: { xs: 0, sm: '1px' } }}>
                                <ButtonField
                                    label={isValidating ? 'Validation…' : 'Valider mon compte'}
                                    variant="contained"
                                    className="font-bold"
                                    onClick={handleValidateCode}
                                    disabled={isValidating || !code.trim()}
                                    startIcon={<HowToRegIcon />}
                                    loading={isValidating}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            )}
        </Settings>
    );
};

export default CooperativeConfirmationStep;
